import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

interface FormInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  type?: string
  placeholder?: string
  helpText?: string
  disabled?: boolean
  required?: boolean
  min?: number | string
  max?: number | string
}

export function FormInput<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  type = 'text',
  placeholder,
  helpText,
  disabled,
  required,
  min,
  max,
}: FormInputProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      <input
        {...field}
        value={field.value ?? ''}
        id={name}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        className={`form-control ${error ? 'is-invalid' : ''}`.trim()}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {helpText && !error && <div className="form-text">{helpText}</div>}
      {error && (
        <div id={`${name}-error`} className="invalid-feedback d-block">
          {error.message}
        </div>
      )}
    </div>
  )
}
